import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../../store';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  fetchProjectResources,
  removeResourceFromProject,
  requestResourceAccess,
} from '../../store/actions/Projects/ProjectActions';
import { showModal, hideModal } from '../../store/actions/modalActions';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import Table from '../shared/TailwindComponents/Table';
import FilterBarWithTabs from '../shared/TailwindComponents/FilterBarWithTabs';
import Badge from '../shared/TailwindComponents/Badge';
import { BadgeColor } from '../shared/TailwindComponents/types/badgeTypes';
import {
  PiTrashLight,
  PiBriefcaseLight,
  PiCheckSquareOffsetLight,
  PiTableLight,
  PiFileMagnifyingGlassLight,
  PiBookOpenTextLight,
  PiDatabaseLight,
  PiFileTextLight,
  PiFileLight,
  PiLinkSimpleHorizontalLight,
  PiArrowSquareOutLight,
  PiLockKeyLight,
  PiHandPalmLight,
  PiClockCountdownLight,
} from 'react-icons/pi';
import { formatDateTime } from '../../utils/functions';
import debounce from 'lodash.debounce';

// ─── Resource type config ───────────────────────────────────────

const RESOURCE_TYPE_CONFIG: Record<string, { label: string; singular: string; icon: React.ReactElement; color: BadgeColor; route: string }> = {
  matter: { label: 'Matters', singular: 'Matter', icon: <PiBriefcaseLight className="w-4 h-4" />, color: 'blue', route: '/matters' },
  task: { label: 'Tasks', singular: 'Task', icon: <PiCheckSquareOffsetLight className="w-4 h-4" />, color: 'green', route: '/tasks' },
  table: { label: 'Tables', singular: 'Table', icon: <PiTableLight className="w-4 h-4" />, color: 'teal', route: '/tables' },
  review: { label: 'Playbooks', singular: 'Playbook', icon: <PiBookOpenTextLight className="w-4 h-4" />, color: 'emerald', route: '/playbooks' },
  playbook: { label: 'Playbooks', singular: 'Playbook', icon: <PiBookOpenTextLight className="w-4 h-4" />, color: 'emerald', route: '/playbooks' },
  review_session: { label: 'Reviews', singular: 'Review', icon: <PiFileMagnifyingGlassLight className="w-4 h-4" />, color: 'sky', route: '/reviews' },
  datasource: { label: 'Data Sources', singular: 'Data Source', icon: <PiDatabaseLight className="w-4 h-4" />, color: 'cyan', route: '/data-sources' },
  template: { label: 'Templates', singular: 'Template', icon: <PiFileTextLight className="w-4 h-4" />, color: 'slate', route: '/templates' },
};

interface ProjectResourcesTabProps {
  projectUuid: string;
  onOpenLinkPanel: () => void;
}

const ProjectResourcesTab: React.FC<ProjectResourcesTabProps> = ({ projectUuid, onOpenLinkPanel }) => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { t } = useTranslation();

  const { resources, isLoadingResources } = useSelector((state: RootState) => state.projects);

  const [typeFilter, setTypeFilter] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('Linked');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  useEffect(() => {
    dispatch(fetchProjectResources(projectUuid));
  }, [dispatch, projectUuid]);

  // ─── Flatten + filter resources ───────────────────────────────

  const allResources: any[] = useMemo(() => {
    if (!resources?.resources) return [];
    const flat: any[] = [];
    for (const [type, items] of Object.entries(resources.resources)) {
      if (typeFilter && type !== typeFilter) continue;
      for (const item of items) flat.push(item);
    }
    // Search - restricted resources have no title, keep them visible unless searching
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      return flat.filter((r) => r.restricted || (r.title || '').toLowerCase().includes(term));
    }
    return flat;
  }, [resources, typeFilter, searchTerm]);

  // ─── Handlers ─────────────────────────────────────────────────

  const [requestedIds, setRequestedIds] = useState<Set<string>>(new Set());

  const handleRequestAccess = async (resourceType: string, linkId: number) => {
    await dispatch(requestResourceAccess(projectUuid, resourceType, undefined, undefined, linkId));
    setRequestedIds((prev) => new Set(prev).add(`link-${linkId}`));
  };

  const handleUnlink = (resourceType: string, resourceUuid: string, resourceTitle: string) => {
    dispatch(showModal({
      showModal: true, type: 'confirmation',
      title: t('Unlink resource'),
      message: t('Remove "{{name}}" from this project? The resource itself will not be deleted.', { name: resourceTitle }),
      onConfirm: async () => { await dispatch(removeResourceFromProject(projectUuid, resourceType, resourceUuid)); dispatch(hideModal()); },
    }));
  };

  const handleBulkUnlink = () => {
    dispatch(showModal({
      showModal: true, type: 'confirmation',
      title: t('Unlink selected resources'),
      message: t('Remove {{count}} resource(s) from this project?', { count: selectedIds.length }),
      onConfirm: async () => {
        for (const id of selectedIds) {
          // Restricted items use `restricted-${link_id}` as their row id and are
          // excluded from selection (the table rows are non-selectable for them),
          // so every id here is guaranteed to be a `${resource_type}-${resource_uuid}`
          // from an accessible resource. The resource_uuid lookup is therefore safe.
          const resource = allResources.find((r) => `${r.resource_type}-${r.resource_uuid}` === id);
          if (resource) await dispatch(removeResourceFromProject(projectUuid, resource.resource_type, resource.resource_uuid));
        }
        setSelectedIds([]);
        dispatch(hideModal());
        dispatch(showFeedbackModal({ showModal: true, modalType: 'success', message: t('Resources unlinked'), duration: 3000 }));
      },
    }));
  };

  const handleSort = (column: string, order: 'asc' | 'desc') => {
    setSortBy(column);
    setSortOrder(order);
  };

  const debouncedSearch = useCallback(debounce((term: string) => { setSearchTerm(term); }, 300), []);

  // ─── Tabs (resource type filter) ──────────────────────────────

  const availableTypes = resources?.resources ? Object.keys(resources.resources) : [];

  const resourceFilters = availableTypes.length > 0 ? [{
    id: 'type',
    name: t('Resource type'),
    options: availableTypes.map((type) => ({
      value: type,
      label: t(RESOURCE_TYPE_CONFIG[type]?.label || type),
    })),
  }] : [];

  // ─── Filter bar actions (bulk) ────────────────────────────────

  const filterActions = selectedIds.length > 0
    ? [{ label: t('Unlink selected ({{count}})', { count: selectedIds.length }), onClick: handleBulkUnlink, icon: <PiTrashLight className="w-4 h-4" /> }]
    : [];

  // ─── Table columns & data ─────────────────────────────────────

  const columns = ['Resource', 'Type', 'Status', 'Linked', 'Actions'];

  const sortedResources = useMemo(() => {
    const sorted = [...allResources];
    sorted.sort((a, b) => {
      let aVal: any, bVal: any;
      if (sortBy === 'Resource') { aVal = (a.title || '').toLowerCase(); bVal = (b.title || '').toLowerCase(); }
      else if (sortBy === 'Type') { aVal = a.resource_type; bVal = b.resource_type; }
      else if (sortBy === 'Status') { aVal = a.status || ''; bVal = b.status || ''; }
      else { aVal = a.linked_at || ''; bVal = b.linked_at || ''; }
      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [allResources, sortBy, sortOrder]);

  const tableData = useMemo(() =>
    sortedResources.map((resource) => {
      const config = RESOURCE_TYPE_CONFIG[resource.resource_type] || { label: resource.resource_type, singular: resource.resource_type, icon: <PiFileLight className="w-4 h-4" />, color: 'gray' as BadgeColor, route: '' };
      const id = resource.restricted ? `restricted-${resource.link_id}` : `${resource.resource_type}-${resource.resource_uuid}`;

      if (resource.restricted) {
        const reqKey = `link-${resource.link_id}`;
        // Use backend status if available, otherwise check local optimistic state
        const backendStatus = resource.access_request_status;
        const hasLocalRequest = requestedIds.has(reqKey);
        const requestStatus = backendStatus || (hasLocalRequest ? 'pending' : null);

        const statusLabel = requestStatus === 'pending' ? (
          <span className="inline-flex items-center gap-0.5 text-[11px] text-warning-600 dark:text-warning-400 font-medium">
            <PiClockCountdownLight className="w-3 h-3" />
            {t('Pending')}
          </span>
        ) : requestStatus === 'denied' ? (
          <button
            onClick={(e) => { e.stopPropagation(); handleRequestAccess(resource.resource_type, resource.link_id); }}
            className="text-[11px] text-primary-600 dark:text-primary-400 hover:underline flex items-center gap-0.5"
          >
            <PiHandPalmLight className="w-3 h-3" />
            {t('Request again')}
          </button>
        ) : requestStatus === 'approved' ? (
          <span className="inline-flex items-center gap-0.5 text-[11px] text-success-600 dark:text-success-400 font-medium">
            {t('Approved')}
          </span>
        ) : (
          <button
            onClick={(e) => { e.stopPropagation(); handleRequestAccess(resource.resource_type, resource.link_id); }}
            className="text-[11px] text-primary-600 dark:text-primary-400 hover:underline flex items-center gap-0.5"
          >
            <PiHandPalmLight className="w-3 h-3" />
            {t('Request access')}
          </button>
        );

        return {
          id,
          _resource: resource,
          _restricted: true,
          'Resource': (
            <div className="flex items-start gap-2.5">
              <PiLockKeyLight className="w-4 h-4 text-gray-400 dark:text-gray-500 flex-shrink-0 mt-0.5" />
              <div className="min-w-0">
                <span className="text-sm text-gray-400 dark:text-gray-500 italic">{t('Restricted {{type}}', { type: t(config.singular) })}</span>
                <div className="mt-0.5">{statusLabel}</div>
              </div>
            </div>
          ),
          'Type': <Badge color="gray">{t(config.singular)}</Badge>,
          'Status': <span className="text-sm text-gray-400">{'-'}</span>,
          'Linked': resource.linked_at ? <span className="text-sm text-gray-400">{formatDateTime(resource.linked_at)}</span> : <span className="text-sm text-gray-400">{'-'}</span>,
        };
      }

      return {
        id,
        _resource: resource,
        _restricted: false,
        'Resource': (
          <div className="flex items-center gap-2.5">
            <span className="text-gray-400 dark:text-gray-500 flex-shrink-0">{config.icon}</span>
            <span className="text-sm text-gray-900 dark:text-white truncate">{resource.title || t('Untitled')}</span>
          </div>
        ),
        'Type': <Badge color={config.color}>{t(config.singular)}</Badge>,
        'Status': resource.status ? <span className="text-sm text-gray-600 dark:text-gray-400 capitalize">{resource.status}</span> : <span className="text-sm text-gray-400">{'-'}</span>,
        'Linked': <span className="text-sm text-gray-500 dark:text-gray-400">{formatDateTime(resource.linked_at)}</span>,
      };
    }),
  [sortedResources, t]);

  // ─── Table actions ────────────────────────────────────────────

  const tableActions = [
    {
      label: t('Open'),
      onClick: (row: any) => {
        if (row._restricted) return;
        const r = row._resource;
        const config = RESOURCE_TYPE_CONFIG[r.resource_type];
        if (config) history.push(`${config.route}/${r.resource_uuid}`);
      },
      icon: <PiArrowSquareOutLight className="w-4 h-4" />,
      hidden: (row: any) => row._restricted,
    },
    {
      label: t('Unlink'),
      onClick: (row: any) => {
        if (row._restricted) return;
        const r = row._resource;
        handleUnlink(r.resource_type, r.resource_uuid, r.title);
      },
      icon: <PiTrashLight className="w-4 h-4" />,
      isDestructive: true,
      hidden: (row: any) => row._restricted,
    },
  ];

  // ─── Render ───────────────────────────────────────────────────

  return (
    /*
      NO FIXED HEIGHT. This used to be `h-[calc(100vh-95px)]`, guessed from
      the chrome above it. It renders inside the detail surface's canvas now,
      which already gives it a bounded height - a second guess at the same
      number produces two scrollbars the moment either changes.
    */
    <div className="flex flex-col h-full min-h-0">
      <FilterBarWithTabs
        itemCount={sortedResources.length}
        totalItemCount={resources?.total || 0}
        itemCountLabel="resources"
        filters={resourceFilters}
        selectedFilters={typeFilter ? { type: [typeFilter] } : {}}
        onFilterChange={(filterName: string, values: string[]) => setTypeFilter(values[0] || '')}
        onSearchChange={debouncedSearch}
        actions={filterActions}
        onClearAllFilters={() => { setTypeFilter(''); setSearchTerm(''); }}
        selectionCount={selectedIds.length > 0 ? selectedIds.length : undefined}
        // itemCount={sortedResources.length}
        // totalItemCount={resources?.accessible_count || resources?.total || 0}
        // itemCountLabel={t('resources')}
      />

      <div className="flex-1 min-h-0 relative flex flex-col">
        <Table
          columns={columns}
          data={tableData}
          enableSelection
          selectedIds={selectedIds}
          onSelect={(id: string) => {
            if (id.startsWith('restricted-')) return;
            setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
          }}
          onSelectAll={() => {
            const selectableResources = sortedResources.filter((r) => !r.restricted);
            setSelectedIds(selectedIds.length === selectableResources.length ? [] : selectableResources.map((r) => `${r.resource_type}-${r.resource_uuid}`));
          }}
          onRowClick={(row: any) => {
            if (row._restricted) return;
            const r = row._resource;
            const config = RESOURCE_TYPE_CONFIG[r.resource_type];
            if (config) history.push(`${config.route}/${r.resource_uuid}`);
          }}
          actions={tableActions}
          sortableColumns={['Resource', 'Type', 'Status', 'Linked']}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
          total_items={resources?.total || 0}
          total_showing_items={sortedResources.length}
          setIsShowing={true}
          showingLabel={t('resources')}
          selectedCount={selectedIds.length}
          resizableColumns
          storageKeyPrefix="project-resources"
          // The surface owns the height; the table just fills what it is given.
          max="max-h-full"
          min="min-h-0"
          emptyState={{
            icon: <PiLinkSimpleHorizontalLight className="w-10 h-10 text-gray-300" />,
            headline: t('No resources linked yet'),
            subtext: t('Use the "Link Resource" button to connect matters, tasks, and more.'),
          }}
          rowSize="compact"
        />
      </div>
    </div>
  );
};

export default ProjectResourcesTab;
